import React from 'react';
import { Objective, ObjectiveStatus, Milestone, TrackBy, TRACK_BY_LABELS } from '../../types/pms';
import ProgressBar from './ProgressBar';
import { OBJECTIVES_LOCKED_MESSAGE } from '../../services/pmsWorkflow';
import './ObjectiveCard.css';

interface ObjectiveCardProps {
  objective: Objective;
  milestones?: Milestone[];
  onClick?: (objective: Objective) => void;
  /**
   * Whether HR has locked the whole cycle this objective belongs to.
   *
   * OR-ed with the objective's own `isLocked` below. An HR lock is written to the cycle row
   * only and never fans out to objective rows, so reading `objective.isLocked` alone showed no
   * lock badge and no message on a card whose controls were in fact frozen — see
   * isObjectiveLockedByHR in pmsWorkflow.
   */
  cycleLocked?: boolean;
}

/**
 * Get CSS class for the objective status badge.
 */
export function getObjectiveStatusClass(status: ObjectiveStatus): string {
  switch (status) {
    case ObjectiveStatus.DRAFT: return 'obj-status-draft';
    case ObjectiveStatus.SUBMITTED: return 'obj-status-submitted';
    case ObjectiveStatus.APPROVED: return 'obj-status-approved';
    case ObjectiveStatus.RETURNED: return 'obj-status-returned';
    case ObjectiveStatus.IN_PROGRESS: return 'obj-status-in-progress';
    case ObjectiveStatus.UNDER_REVIEW: return 'obj-status-under-review';
    case ObjectiveStatus.COMPLETED: return 'obj-status-completed';
    default: return '';
  }
}

/**
 * Check if any milestones are overdue (past due date with progress < 100%).
 */
function hasOverdueMilestones(milestones: Milestone[]): boolean {
  const now = new Date().toISOString();
  return milestones.some(
    (ms) => ms.dueDate < now && ms.progressPercentage < 100
  );
}

/**
 * ObjectiveCard Component
 * Displays objective title, status badge, weightage, progress bar, and lock indicator.
 * Shows overdue indicator for milestones past due date with progress below 100%.
 * Requirements: 5.5, 7.2, 18.1
 */
const ObjectiveCard: React.FC<ObjectiveCardProps> = ({
  objective,
  milestones = [],
  onClick,
  cycleLocked = false,
}) => {
  const overdue = hasOverdueMilestones(milestones);
  /** Either the objective's own lock or an HR lock on its cycle — both freeze editing. */
  const locked = objective.isLocked || cycleLocked;

  const handleClick = () => {
    if (onClick) onClick(objective);
  };

  return (
    <div
      className="pms-objective-card"
      onClick={handleClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }
          : undefined
      }
    >
      <div className="pms-objective-card__header">
        <h3 className="pms-objective-card__title">{objective.title}</h3>
        <div className="pms-objective-card__badges">
          {locked && (
            <span
              className="pms-objective-card__lock"
              aria-label="Locked by HR"
              title={OBJECTIVES_LOCKED_MESSAGE}
            >
              🔒
            </span>
          )}
          <span className={`pms-objective-card__status ${getObjectiveStatusClass(objective.status)}`}>
            {objective.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="pms-objective-card__meta">
        <span className="pms-objective-card__weightage">
          Weight: {objective.weightage}%
        </span>
        <span className="pms-objective-card__track-by" title="How this objective's progress is tracked">
          {objective.trackBy === TrackBy.RATING ? '⭐' : '📊'} {TRACK_BY_LABELS[objective.trackBy || TrackBy.PERCENT]}
        </span>
        {overdue && (
          <span className="pms-objective-card__overdue">⚠ Overdue milestones</span>
        )}
      </div>
      {locked && (
        <p className="pms-objective-card__lock-message">{OBJECTIVES_LOCKED_MESSAGE}</p>
      )}

      <ProgressBar value={objective.progressPercentage} size="sm" />
    </div>
  );
};

export default ObjectiveCard;
